import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { FileUpload } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";

export default function Masters() {
    const toast = useRef(null);

    const [masters, setMasters] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingMaster, setEditingMaster] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        id: null,
        platformid: '',
        master_type: '',
        master_details: '',
        master_image: null,
        master_image_url: null,
        status: 'Active',
        effective_from: null,
        data_transfer: ''
    });

    const dataTransferOptions = [
        { label: 'Updated', value: 'updated' },
        { label: 'Pending', value: 'pending' }
    ];

    const [errors, setErrors] = useState({});

    // Fetch all masters
    const fetchMasters = async () => {
        setLoading(true);
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/masters_creation');
            setMasters(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false); //stop loading
        }
    };

    // Fetch all platforms
    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms((res.data || []).map((p) => ({
                label: p.platform_name,
                value: String(p.platform_id),
            })));
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchMasters();
        fetchPlatforms();
    }, []);

    // Handle text/date/dropdown changes
    const onInputChange = (e, name) => {
        setFormData({ ...formData, [name]: e.target.value });
    };

    // Handle file change
    const onFileSelect = (e) => {
        setFormData({ ...formData, master_image: e.files[0] });
    };

    // Validate form
    const validateForm = () => {
        let newErrors = {};

        if (!formData.platformid || !formData.platformid.trim()) {
            newErrors.platformid = "Platform is required";
        }

        if (!formData.master_type || !formData.master_type.trim()) {
            newErrors.master_type = "Master type is required";
        }

        if (!formData.master_details || !formData.master_details.trim()) {
            newErrors.master_details = "Master detail is required";
        }

        if (!formData.effective_from) {
            newErrors.effective_from = "Effective from is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    // Save or Update Master
    const saveMaster = async () => {
        if (!validateForm()) {
            toast.current.show({ severity: "error", summary: "Validation Error", detail: "Please fix errors before saving" });
            return;
        }

        try {
            const url = editingMaster ? 'https://treatway.co.in/Counter-Backend/api/server_masters/save/masters_creation' : 'https://treatway.co.in/Counter-Backend/api/server_masters/save/masters_creation';

            const data = new FormData();
            data.append("id", formData.id || "");
            data.append("platformid", formData.platformid);
            data.append("master_type", formData.master_type);
            data.append("master_details", formData.master_details);
            data.append("status", formData.status);
            data.append("effective_from", formData.effective_from ? formData.effective_from.toISOString().split("T")[0] : "");
            if (formData.data_transfer) data.append('data_transfer', formData.data_transfer);
            if (formData.master_image_url) {
                data.append("master_image_url", formData.master_image_url);
            }
            if (formData.master_image) {
                data.append("master_image", formData.master_image);
            }

            await axios.post(url, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: editingMaster ? "Master Updated" : "Master Added"
            });

            setVisible(false);
            setEditingMaster(null);
            setFormData({ id: null, platformid: "", master_type: "", master_details: "", master_image: null, status: "Active", effective_from: null });
            fetchMasters(); // refresh table
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
        }
    };

    // Open dialog for edit
    const editMaster = async (rowData) => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/get_master_by_id/${rowData.master_id}`);

            const data = res.data;

            setEditingMaster(true);
            setFormData({
                id: data.master_id,
                platformid: data.platform_id ? String(data.platform_id) : "",
                master_type: data.master_type,
                master_details: data.master_details,
                master_image: null,
                master_image_url: data.master_image_path,
                status: data.master_status,
                effective_from: null
            });

            setVisible(true);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load master details" });
        }
    };

    // Dialog footer
    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveMaster} />
        </div>
    );

    const statusOptions = [
        { label: "Active", value: "Active" },
        { label: "InActive", value: "InActive" }
    ];

    return (
        <>
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Masters</h2>
            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <Button
                            label="Add Master"
                            icon="pi pi-plus"
                            onClick={() => {
                                const posPlatform = platforms.find((p) => p.label === 'POS');

                                setVisible(true);
                                setEditingMaster(null);
                                setFormData({
                                    id: null,
                                    platformid: posPlatform ? posPlatform.value : '',
                                    master_type: '',
                                    master_details: '',
                                    master_image: null,
                                    status: 'Active',
                                    effective_from: new Date(),
                                    data_transfer: ''
                                });
                            }}
                        />
                    </div>
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={masters}
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '60rem' }}
                    className="p-datatable-sm relative"
                    loading={loading}
                    loadingIcon={
                        //custom overlay loader
                        <div className="flex align-items-center justify-content-center w-full h-full bg-black-alpha-30 absolute top-0 left-0">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".7s" />
                        </div>
                    }
                    emptyMessage="No masters found."
                    globalFilter={globalFilter}
                >
                    <Column field="master_type" header="Type" />
                    <Column field="master_details" header="Details" />
                    <Column header="Actions" body={(rowData) => <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => editMaster(rowData)} />} />
                </DataTable>

                <Dialog header={editingMaster ? 'Edit Master' : 'Add Master'} visible={visible} style={{ width: '450px' }} footer={dialogFooter} onHide={() => setVisible(false)}>
                    <div className="field">
                        <label htmlFor="platformid">Platform</label>
                        <Dropdown id="platformid" value={formData.platformid} options={platforms} onChange={(e) => setFormData({ ...formData, platformid: e.value })} placeholder="Select a Platform" className="w-full" />
                        {errors.platformid && <small className="p-error">{errors.platformid}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_type">Master Type</label>
                        <InputText id="master_type" value={formData.master_type} onChange={(e) => onInputChange(e, 'master_type')} className="w-full" />
                        {errors.master_type && <small className="p-error">{errors.master_type}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_details">Master Details</label>
                        <InputText id="master_details" value={formData.master_details} onChange={(e) => onInputChange(e, 'master_details')} className="w-full" />
                        {errors.master_details && <small className="p-error">{errors.master_details}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="master_image">Master Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setFormData({ ...formData, master_image: file, preview: URL.createObjectURL(file) });
                                }
                            }}
                            className="w-full"
                        />
                        {formData.preview && <img src={formData.preview} alt="Preview" className="mt-2 border rounded" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />}
                        {/* Show old image if editing and no new file chosen */}
                        {!formData.preview && editingMaster && formData.master_image_url && (
                            <img src={`https://treatway.co.in/Counter-Backend/uploads/masters/${formData.master_image_url}`} alt="Current" className="mt-2 border rounded" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                        )}
                    </div>

                    <div className="field">
                        <label htmlFor="status">Status</label>
                        <Dropdown id="status" value={formData.status} options={statusOptions} onChange={(e) => setFormData({ ...formData, status: e.value })} className="w-full" />
                    </div>

                    <div className="grid p-fluid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="effective_from">
                                    Effective From <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Calendar id="effective_from" value={formData.effective_from} onChange={(e) => setFormData({ ...formData, effective_from: e.value })} dateFormat="dd-mm-yy" showIcon className="w-full" />
                                {errors.effective_from && <small className="p-error">{errors.effective_from}</small>}
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="data_transfer">Data Transfer</label>
                                <Dropdown id="data_transfer" value={formData.data_transfer} options={dataTransferOptions} onChange={(e) => setFormData({ ...formData, data_transfer: e.value })} placeholder="Select Data Transfer" className="w-full" />
                            </div>
                        </div>
                    </div>
                </Dialog>
            </div>
        </>
    );
}
